/* eslint react-hooks/exhaustive-deps: "warn" */

import { useCallback, useEffect, useRef, useState } from 'react';

import cn from 'classnames';

import { Link } from 'libs/router/Link';

import { useDraggableScroll } from 'hooks/useDraggableScroll';

import { Icon } from 'ui/atoms/Icon/Icon';

import styles from './Tabs.module.scss';

import type { TabItem, TabsProps } from './Tabs.types';

export const Tabs = ({
  tabs,
  activeKey,
  defaultActiveKey,
  onChange,
  size = 'md',
  noBorder = false,
  variant = 'default',
  theme = 'light',
  className,
  scrollable = false,
}: TabsProps) => {
  const [internalKey, setInternalKey] = useState<string>(defaultActiveKey ?? tabs[0]?.key ?? '');

  const barRef = useRef<HTMLDivElement>(null);
  const [overflowing, setOverflowing] = useState(false);
  const [canLeft, setCanLeft] = useState(false);
  const [canRight, setCanRight] = useState(false);

  const current = activeKey ?? internalKey;
  const hasContent = tabs.some((t) => t.content !== undefined);

  const { onLeftTransition, onRightTransition, recalcTransitions } = useDraggableScroll(barRef, [tabs, scrollable]);

  const updateEdges = useCallback(() => {
    const el = barRef.current;
    if (!el) {
      return;
    }

    const maxScroll = el.scrollWidth - el.clientWidth;

    setOverflowing(maxScroll > 1);
    setCanLeft(el.scrollLeft > 1);
    setCanRight(el.scrollLeft < maxScroll - 1);
  }, []);

  useEffect(() => {
    if (!scrollable) {
      return;
    }

    updateEdges();
    recalcTransitions();

    const el = barRef.current;
    if (!el) {
      return;
    }

    el.addEventListener('scroll', updateEdges, { passive: true });
    window.addEventListener('resize', updateEdges);

    return () => {
      el.removeEventListener('scroll', updateEdges);
      window.removeEventListener('resize', updateEdges);
    };
  }, [scrollable, tabs, updateEdges, recalcTransitions]);

  const handleClick = (key: string) => {
    if (activeKey === undefined) setInternalKey(key);
    onChange?.(key);
  };

  const renderTab = (tab: TabItem) => {
    const classes = cn(styles.tab, tab.key === current && styles.tabActive);

    const inner = (
      <>
        {tab.icon &&
          (typeof tab.icon === 'string' ? (
            <Icon type={tab.icon} size="md" className={styles.icon} />
          ) : (
            <span className={styles.icon}>{tab.icon}</span>
          ))}

        {tab.label}
      </>
    );

    if (tab.route !== undefined) {
      return (
        <Link key={tab.key} route={tab.route} query={tab.query} className={classes}>
          {inner}
        </Link>
      );
    }

    return (
      <button key={tab.key} type="button" className={classes} onClick={() => handleClick(tab.key)}>
        {inner}
      </button>
    );
  };

  const bar = (
    <div
      ref={barRef}
      className={cn(
        styles.bar,
        size === 'xl' ? styles.barXl : size === 'lg' ? styles.barLg : size === 'sm' ? styles.barSm : styles.barMd,
        noBorder && styles.barNoBorder,
        variant === 'underline' && styles.barUnderline,
        variant === 'modern' && styles.barModern,
        theme === 'dark' && styles.barDark,
        scrollable && styles.barScrollable,
      )}
    >
      {tabs.map(renderTab)}
    </div>
  );

  return (
    <div className={cn(styles.root, className)}>
      {scrollable ? (
        <div className={cn(styles.scroller, canLeft && styles.fadeLeft, canRight && styles.fadeRight)}>
          {overflowing && (
            <button
              type="button"
              aria-label="Scroll left"
              disabled={!canLeft}
              onClick={onLeftTransition}
              className={cn(styles.arrow, styles.arrowLeft, !canLeft && styles.arrowHidden)}
            >
              <Icon type="arrow-8" />
            </button>
          )}

          {bar}

          {overflowing && (
            <button
              type="button"
              aria-label="Scroll right"
              disabled={!canRight}
              onClick={onRightTransition}
              className={cn(styles.arrow, styles.arrowRight, !canRight && styles.arrowHidden)}
            >
              <Icon type="arrow-9" />
            </button>
          )}
        </div>
      ) : (
        bar
      )}

      {hasContent && (
        <div className={styles.panels}>
          {tabs.map((tab) => (
            <div
              key={tab.key}
              className={cn(styles.panel, tab.key === current && styles.panelActive)}
              aria-hidden={tab.key !== current}
            >
              {tab.content}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
